import TeacherPageHeader from '../../components/TeacherPageHeader';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI } from '../../services';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import Loading from '../../components/Loading';
import { User } from '../../types/api';

type SectionType = 'email' | 'password';

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const { auth, updateUserInfo } = useAuth();
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState<SectionType>('email');
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [submitting, setSubmitting] = useState(false);

  // 이메일 변경 관련 상태
  const [emailForm, setEmailForm] = useState({ newEmail: '' });
  const [submittingEmail, setSubmittingEmail] = useState(false);

  // 현재 이메일 초기화
  useEffect(() => {
    if (auth.userInfo && 'email' in auth.userInfo) {
      setEmailForm({ newEmail: (auth.userInfo as User).email || '' });
    }
  }, [auth.userInfo]);

  if (!auth.isAuthenticated || auth.userType !== 'user') {
    return (
      <div className="user-page user-lesson-schedule-page">
        <div className="page-content">
          <div className="container">
            <div className="error">{t('access_denied')}</div>
          </div>
        </div>
      </div>
    );
  }

  const currentEmail = (auth.userInfo as User)?.email || '';

  const onSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailForm.newEmail.trim()) {
      alert(t('email_required_error'));
      return;
    }

    if (emailForm.newEmail.trim() === currentEmail) {
      alert(t('email_already_same'));
      return;
    }

    try {
      setSubmittingEmail(true);
      const resp = await userAPI.updateEmail(emailForm.newEmail.trim());
      if (resp.success) {
        alert(t('email_update_success_toast'));
        if (auth.userInfo) {
          updateUserInfo({ ...auth.userInfo, email: emailForm.newEmail.trim() });
        }
      } else {
        alert(resp.message || t('email_update_failed_toast'));
      }
    } catch {
      alert(t('email_update_failed_toast'));
    } finally {
      setSubmittingEmail(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.currentPassword || !form.newPassword) {
      alert(t('fill_required_fields'));
      return;
    }

    if (form.newPassword.length < 6) {
      alert(t('password_min_length'));
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      alert(t('password_mismatch'));
      return;
    }

    try {
      setSubmitting(true);
      const resp = await userAPI.changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      if (resp.success) {
        alert(t('password_change_success'));
        navigate('/user/dashboard');
      } else {
        alert(resp.message || t('password_change_failed'));
      }
    } catch {
      alert(t('password_change_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="user-page account-settings-page">
      <TeacherPageHeader title="계정 설정" />
      <div className="page-content">
        <div className="container">
          <div className="settings-layout">
            {/* 보조 사이드바 */}
            <aside className="settings-sidebar">
              <nav className="sidebar-nav">
                <button
                  className={`sidebar-item ${activeSection === 'email' ? 'active' : ''}`}
                  onClick={() => setActiveSection('email')}
                >
                  {t('edit_email_section')}
                </button>
                <button
                  className={`sidebar-item ${activeSection === 'password' ? 'active' : ''}`}
                  onClick={() => setActiveSection('password')}
                >
                  {t('change_password')}
                </button>
              </nav>
            </aside>

            {/* 메인 콘텐츠 */}
            <main className="settings-content">
              {/* 이메일 변경 섹션 */}
              {activeSection === 'email' && (
                <div className="settings-section">
                  <div className="section-header">
                    <h3 className="section-title">{t('edit_email_section')}</h3>
                  </div>
                  <form onSubmit={onSubmitEmail}>
                    <div className="form-group">
                      <label className="form-label">{t('current_email')}</label>
                      <input
                        type="email"
                        className="form-input"
                        value={currentEmail}
                        disabled
                        style={{ backgroundColor: '#f5f5f5' }}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="newEmail" className="form-label">{t('new_email')}</label>
                      <input
                        id="newEmail"
                        type="email"
                        className="form-input"
                        value={emailForm.newEmail}
                        onChange={(e) => setEmailForm({ newEmail: e.target.value })}
                        required
                      />
                    </div>

                    <div className="form-actions">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={submittingEmail || emailForm.newEmail.trim() === currentEmail}
                      >
                        {submittingEmail ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Loading size="small" message="" />
                            {t('saving')}
                          </div>
                        ) : (
                          t('update_email')
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* 비밀번호 변경 섹션 */}
              {activeSection === 'password' && (
                <div className="settings-section">
                  <div className="section-header">
                    <h3 className="section-title">{t('change_password')}</h3>
                  </div>
                  <div className="notice-text">
                    {t('password_minimum_length_notice')}
                  </div>

                  <form onSubmit={onSubmit}>
                    <div className="form-group">
                      <label htmlFor="currentPassword" className="form-label">{t('current_password')}</label>
                      <input
                        id="currentPassword"
                        type="password"
                        className="form-input"
                        value={form.currentPassword}
                        onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="newPassword" className="form-label">{t('new_password')}</label>
                      <input
                        id="newPassword"
                        type="password"
                        className="form-input"
                        value={form.newPassword}
                        onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                        minLength={6}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword" className="form-label">{t('confirm_new_password')}</label>
                      <input
                        id="confirmPassword"
                        type="password"
                        className="form-input"
                        value={form.confirmPassword}
                        onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                        required
                      />
                    </div>

                    <div className="form-actions">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Loading size="small" message="" />
                            {t('changing')}
                          </div>
                        ) : (
                          t('change_password')
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
